/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import { FlatList, View } from 'react-native';
import DyteClient, { SocketConnectionState } from '@dytesdk/web-core';
import { UIConfig } from '../types/ui-config';
import { IconPack, defaultIconPack } from '../lib/icons';
import DyteNotification from './DyteNotification';
import { DyteI18n, useLanguage } from '../lib/lang';
import SoundPlayer from 'react-native-sound-player';
import { States } from '../types/props';
import { defaultConfig } from '../lib/default-ui-config';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function Notification({
  meeting,
  config = defaultConfig,
  iconPack = defaultIconPack,
  size = 'sm',
  states,
  t,
}: {
  meeting: DyteClient;
  config: UIConfig;
  iconPack: IconPack;
  size: 'lg' | 'md' | 'sm' | 'xl';
  states: States;
  t: DyteI18n;
}) {
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates } = useContext(DyteUIContext);
  states = states ?? storeStates;
  const muteNotificationSounds = states.prefs.muteNotificationSounds;
  const [notifications, setNotifications] = useState([]);
  const [pollsState, setPollsState] = useState([]);
  const [notificationDuration, setNotificationDuration] = useState(1000);

  const hasExistingNotification = useCallback(
    (message: string) => {
      return notifications.some((notification) => notification === message);
    },
    [notifications]
  );

  const addNotification = useCallback(
    (message) => {
      if (!hasExistingNotification(message)) {
        setNotifications((prevNotifications) => [
          ...prevNotifications,
          message,
        ]);
      }
    },
    [hasExistingNotification]
  );

  const removeNotification = useCallback((message) => {
    setNotifications((prevNotifications) =>
      prevNotifications.filter((item) => item !== message)
    );
  }, []);

  useEffect(() => {
    let _onFinishedPlayingSubscription = null;
    let _onFinishedLoadingSubscription = null;
    let _onFinishedLoadingFileSubscription = null;
    let _onFinishedLoadingURLSubscription = null;

    _onFinishedPlayingSubscription = SoundPlayer.addEventListener(
      'FinishedPlaying',
      ({ success }) => {
        if (!success) {
          console.error('Error in FinishedPlaying event');
        }
      }
    );
    _onFinishedLoadingSubscription = SoundPlayer.addEventListener(
      'FinishedLoading',
      ({ success }) => {
        if (!success) {
          console.error('Error in FinishedLoading event');
        }
      }
    );
    _onFinishedLoadingFileSubscription = SoundPlayer.addEventListener(
      'FinishedLoadingFile',
      ({ success, name, type }) => {
        if (!success) {
          console.error(
            `Error in FinishedLoadingFile event for file: ${name}.${type}`
          );
        }
      }
    );
    _onFinishedLoadingURLSubscription = SoundPlayer.addEventListener(
      'FinishedLoadingURL',
      ({ success, url }) => {
        if (!success) {
          console.error(`Error in FinishedLoadingURL event for URL: ${url}`);
        }
      }
    );

    const roomJoinedListener = (participant) => {
      try {
        addNotification(`${participant.name} joined`);
        if (!muteNotificationSounds) {
          SoundPlayer.playSoundFile('notification_join', 'mp3');
        }
      } catch (e) {
        console.error('Error handling participantJoined event:', e);
      }
    };

    const roomLeftListener = (participant) => {
      try {
        addNotification(`${participant.name} left`);
        if (!muteNotificationSounds) {
          SoundPlayer.playSoundFile('notification_join', 'mp3');
        }
      } catch (e) {
        console.error('Error handling participantLeft event:', e);
      }
    };

    const pollListener = ({ polls }) => {
      try {
        if (pollsState.length !== polls.length) {
          addNotification(
            `${polls[polls.length - 1].createdBy} started a poll`
          );
        }
        if (!muteNotificationSounds) {
          SoundPlayer.playSoundFile('notification_message', 'mp3');
        }
      } catch (e) {
        console.error('Error handling pollsUpdate event:', e);
      }
      setPollsState(polls);
    };

    const chatListener = ({ message }) => {
      try {
        addNotification(`${message.displayName} sent a chat`);
        if (!muteNotificationSounds) {
          SoundPlayer.playSoundFile('notification_message', 'mp3');
        }
      } catch (e) {
        console.error('Error handling chatUpdate event:', e);
      }
    };

    const disconnectedHandler = () => {
      try {
        if (!hasExistingNotification(t('network.lost_extended'))) {
          addNotification(t('network.lost_extended'));
        }
        if (!muteNotificationSounds) {
          SoundPlayer.playSoundFile('notification_join', 'mp3');
        }
      } catch (err) {
        console.error('Error handling disconnected event:', err);
      }
    };

    const socketFailureHandler = () => {
      try {
        if (!hasExistingNotification(t('network.lost_extended'))) {
          addNotification(t('network.lost_extended'));
        }
        if (!hasExistingNotification(t('network.disconnected'))) {
          addNotification(t('network.disconnected'));
        }
      } catch (err) {
        console.error('Error handling socketFailure event:', err);
      }
    };

    const socketReconnectingHandler = () => {
      try {
        if (!hasExistingNotification(t('network.reconnecting'))) {
          addNotification(t('network.reconnecting'));
        }
      } catch (err) {
        console.error('Error handling socketReconnecting event:', err);
      }
    };

    const connectedHandler = () => {
      try {
        setNotifications([]);
        addNotification(t('network.restored'));
        if (!muteNotificationSounds) {
          SoundPlayer.playSoundFile('notification_join', 'mp3');
        }
      } catch (err) {
        console.error('Error handling connected event:', err);
      }
    };
    const socketHandler = (status: SocketConnectionState) => {
      if (status.state === 'failed') socketFailureHandler();
      if (status.state === 'reconnecting') socketReconnectingHandler();
      if (status.state === 'connected') connectedHandler();
      if (status.state === 'disconnected') disconnectedHandler();
    };
    // Add event listeners
    meeting.meta.on('socketConnectionUpdate', socketHandler);
    meeting.participants.joined.on('participantJoined', roomJoinedListener);
    meeting.participants.joined.on('participantLeft', roomLeftListener);
    meeting.polls.on('pollsUpdate', pollListener);
    meeting.chat.on('chatUpdate', chatListener);

    return () => {
      // Cleanup event listeners
      meeting.meta.off('socketConnectionUpdate', socketHandler);
      meeting.participants.joined.removeListener(
        'participantJoined',
        roomJoinedListener
      );
      meeting.participants.joined.removeListener(
        'participantLeft',
        roomLeftListener
      );
      meeting.polls.removeListener('pollsUpdate', pollListener);
      meeting.chat.removeListener('chatUpdate', chatListener);
      _onFinishedPlayingSubscription.remove();
      _onFinishedLoadingSubscription.remove();
      _onFinishedLoadingURLSubscription.remove();
      _onFinishedLoadingFileSubscription.remove();
    };
  }, [
    meeting,
    muteNotificationSounds,
    t,
    pollsState,
    addNotification,
    removeNotification,
    hasExistingNotification,
  ]);

  const shouldPersist = useCallback(
    (item) => {
      return (
        item === t('network.lost_extended') ||
        item === t('network.disconnected') ||
        item === t('network.reconnecting')
      );
    },
    [t]
  );

  return (
    <View
      // eslint-disable-next-line react-native/no-inline-styles
      style={{
        position: 'absolute',
        left: 10,
        bottom: 10,
        zIndex: 1,
        justifyContent: 'center',
      }}
    >
      {notifications.length > 0 ? (
        <FlatList
          data={notifications}
          keyExtractor={(item, index) => index.toString()}
          renderItem={({ item }) => (
            <DyteNotification
              notification={{
                id: meeting.self.id,
                message: item,
                image: meeting.self.picture,
                duration: shouldPersist(item) ? 0 : notificationDuration,
                button: {
                  text: item.includes('joined') ? 'Say Hello' : '',
                  variant: 'button',
                  onClick: () => {},
                },
              }}
              onDyteNotificationDismiss={() => {
                removeNotification(item);
              }}
            />
          )}
        />
      ) : (
        <View />
      )}
    </View>
  );
}
